import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { createOrReviveActorRow } from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import { deriveMorphemeNoteId } from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { eq } from "@-/com.npmjs.drizzle-orm";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

function normalizeMorphemeNoteText(text: string): string | null {
  const trimmed = text.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export function useUpsertMorphemeNote() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);

  return useMutation({
    mutationFn: async (input: { morpheme: string; referenceNote: string }): Promise<void> => {
      const noteText = normalizeMorphemeNoteText(input.referenceNote);
      const nowIso = new Date().toISOString();

      await actorDb(import.meta.location).transaction(async (tx) => {
        const existing = await tx.query.morphemeNote.findFirst({
          columns: { id: true },
          where: { morpheme: input.morpheme },
        });
        if (existing === undefined && noteText === null) {
          return;
        }
        // One note row per morpheme, with an id derived from it. A missing row is created blank,
        // and the text is always an UPDATE, so it reaches the cloud as a change to that column.
        const id = existing?.id ?? deriveMorphemeNoteId(input.morpheme);
        if (existing === undefined) {
          await createOrReviveActorRow(tx, actorSchema.morphemeNote, {
            createdAt: nowIso,
            id,
            morpheme: input.morpheme,
          });
        }
        await tx
          .update(actorSchema.morphemeNote)
          .set({ text: noteText })
          .where(eq(actorSchema.morphemeNote.id, id));
      });
    },
    mutationKey: ["upsertMorphemeNote"],
  });
}
